import React, { useEffect, useState, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
    X,
    Download,
    ExternalLink,
    ChevronLeft,
    ChevronRight,
    ZoomIn,
    ZoomOut,
    RotateCw,
    FileText,
    FileWarning,
    File,
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DocumentItem {
    /** Unique key */
    id: string;
    /**
     * Full URL of the document.
     * For a newly selected File object, pass a blob URL via URL.createObjectURL(file).
     * For a server resource, pass the absolute URL.
     */
    url: string;
    /** Human-readable label shown as the document title */
    label: string;
    /** Optional subtitle / reference type */
    subtitle?: string;
    /** Optional ISO date string — shown as "Uploaded: DD MMM YYYY" */
    uploadedAt?: string;
    /**
     * Explicit file extension override (e.g. "pdf").
     * If omitted, the extension is inferred from `url`.
     */
    extension?: string;
    /**
     * Raw text content — used only when extension is "txt" and you
     * already have the file contents as a string.
     */
    textContent?: string;
}

interface ViewDocumentModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    documents: DocumentItem[];
    initialIndex?: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Extract the lowercase file extension from a URL or filename. */
const getExtension = (url: string): string =>
    (url.split('?')[0].split('.').pop() ?? '').toLowerCase();

const IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];

const isImage = (ext: string) => IMAGE_TYPES.includes(ext);

const formatLabel = (str: string) =>
    str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (iso?: string) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString('en-ZA', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return iso;
    }
};

// ─── RenderDocumentPreview ────────────────────────────────────────────────────
// Supports: jpg · jpeg · png · gif · webp · svg · pdf · txt · docx · unknown

interface PreviewProps {
    doc: DocumentItem;
    zoom: number;
    rotation: number;
}

const RenderDocumentPreview: React.FC<PreviewProps> = ({ doc, zoom, rotation }) => {
    const ext = doc.extension ?? getExtension(doc.url);

    // ── Images ──────────────────────────────────────────────────────────────
    if (isImage(ext)) {
        return (
            <div className="w-full h-full flex items-center justify-center overflow-auto p-4">
                <img
                    key={doc.url}
                    src={doc.url}
                    alt={doc.label}
                    style={{
                        transform: `scale(${zoom}) rotate(${rotation}deg)`,
                        transition: 'transform 0.25s ease',
                        maxWidth: '100%',
                        maxHeight: '75vh',
                        objectFit: 'contain',
                        borderRadius: '8px',
                    }}
                    onError={(e) => {
                        (e.target as HTMLImageElement).src =
                            'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">🖼️</text></svg>';
                    }}
                />
            </div>
        );
    }

    // ── PDF ─────────────────────────────────────────────────────────────────
    if (ext === 'pdf') {
        return (
            <div className="w-full h-full flex flex-col flex-1 min-h-0">
                <iframe
                    src={doc.url}
                    title={doc.label}
                    className="w-full h-full flex-1 border-0"
                    style={{ minHeight: '72vh' }}
                />
            </div>
        );
    }

    // ── Plain text ───────────────────────────────────────────────────────────
    if (ext === 'txt') {
        return (
            <div className="w-full h-full p-4">
                <textarea
                    readOnly
                    value={doc.textContent ?? ''}
                    className="w-full h-full min-h-[55vh] resize-none rounded-xl border border-border bg-secondary/10 p-4 text-sm font-mono text-foreground focus:outline-none"
                    placeholder="No text content provided."
                />
            </div>
        );
    }

    // ── DOCX ────────────────────────────────────────────────────────────────
    if (ext === 'docx' || ext === 'doc') {
        return (
            <div className="flex flex-col items-center justify-center gap-4 py-16 px-8 text-center">
                <div className="p-5 bg-blue-100 rounded-2xl">
                    <FileText className="w-14 h-14 text-blue-500" />
                </div>
                <p className="font-semibold text-foreground">Word Document (.{ext})</p>
                <p className="text-sm text-muted-foreground max-w-xs">
                    Word documents cannot be previewed directly in the browser.
                    Download the file to open it.
                </p>
                <a
                    href={doc.url}
                    download
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white rounded-xl text-sm font-semibold hover:bg-blue-600 transition-colors"
                >
                    <Download className="w-4 h-4" />
                    Download Document
                </a>
            </div>
        );
    }

    // ── Unsupported ──────────────────────────────────────────────────────────
    return (
        <div className="flex flex-col items-center justify-center gap-4 py-16 px-8 text-center">
            <div className="p-5 bg-amber-100 rounded-2xl">
                <FileWarning className="w-14 h-14 text-amber-500" />
            </div>
            <p className="font-semibold text-foreground">Unsupported File Type</p>
            <p className="text-sm text-muted-foreground max-w-xs">
                <span className="font-mono font-bold">.{ext || 'unknown'}</span> files cannot be
                previewed. You can still download or open the file externally.
            </p>
            <div className="flex items-center gap-3">
                <a
                    href={doc.url}
                    download
                    className="inline-flex items-center gap-2 px-4 py-2 bg-secondary text-foreground rounded-xl text-sm font-semibold hover:bg-secondary/80 transition-colors"
                >
                    <Download className="w-4 h-4" />
                    Download
                </a>
                <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                    <ExternalLink className="w-4 h-4" />
                    Open
                </a>
            </div>
        </div>
    );
};

// ─── Thumbnail helper ─────────────────────────────────────────────────────────

const Thumbnail: React.FC<{ doc: DocumentItem; active: boolean; onClick: () => void }> = ({
    doc,
    active,
    onClick,
}) => {
    const ext = doc.extension ?? getExtension(doc.url);

    return (
        <button
            onClick={onClick}
            className={`
                shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all
                ${active
                    ? 'border-primary scale-105 shadow-md'
                    : 'border-border/40 hover:border-primary/50 opacity-60 hover:opacity-100'
                }
            `}
            aria-label={`View ${doc.label}`}
        >
            {isImage(ext) ? (
                <img src={doc.url} alt={doc.label} className="w-full h-full object-cover" />
            ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-secondary/20">
                    {ext === 'pdf' ? (
                        <FileText className="w-5 h-5 text-red-500" />
                    ) : ext === 'docx' || ext === 'doc' ? (
                        <FileText className="w-5 h-5 text-blue-500" />
                    ) : (
                        <File className="w-5 h-5 text-muted-foreground" />
                    )}
                    <span className="text-[9px] font-bold uppercase text-muted-foreground">
                        .{ext}
                    </span>
                </div>
            )}
        </button>
    );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

const ViewDocumentModal: React.FC<ViewDocumentModalProps> = ({
    open,
    onOpenChange,
    documents,
    initialIndex = 0,
}) => {
    const [current, setCurrent] = useState(initialIndex);
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);

    // Reset on open / initialIndex change
    useEffect(() => {
        if (open) {
            setCurrent(initialIndex);
            setZoom(1);
            setRotation(0);
        }
    }, [open, initialIndex]);

    const doc = documents[current];
    const total = documents.length;
    const hasPrev = current > 0;
    const hasNext = current < total - 1;

    const goTo = useCallback((idx: number) => {
        setCurrent(idx);
        setZoom(1);
        setRotation(0);
    }, []);

    // Keyboard navigation
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (!open) return;
            if (e.key === 'ArrowLeft' && hasPrev) goTo(current - 1);
            if (e.key === 'ArrowRight' && hasNext) goTo(current + 1);
        },
        [open, hasPrev, hasNext, current, goTo]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    if (!doc) return null;

    const currentExt = doc.extension ?? getExtension(doc.url);
    const showImageControls = isImage(currentExt);

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                {/* Overlay */}
                <Dialog.Overlay className="fixed inset-0 z-50 bg-black/20 backdrop-blur-xs animate-in fade-in duration-200" />

                {/* Panel */}
                <Dialog.Content
                    className="
                        fixed z-50
                        top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                        w-[95vw] max-w-6xl h-[92vh]
                        bg-card border border-border/60
                        rounded-2xl shadow-2xl
                        flex flex-col
                        focus:outline-none
                        animate-in fade-in zoom-in-95 duration-200
                    "
                >
                    {/* ══ Header ══════════════════════════════════════════════ */}
                    <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-border/50 shrink-0">
                        <div className="min-w-0 flex-1">
                            <Dialog.Title className="font-bold text-foreground truncate text-base leading-tight">
                                {formatLabel(doc.label)}
                            </Dialog.Title>
                            {doc.subtitle && (
                                <p className="text-[11px] text-muted-foreground uppercase tracking-widest mt-0.5 truncate">
                                    {formatLabel(doc.subtitle)}
                                </p>
                            )}
                        </div>

                        {/* Ext badge */}
                        <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-muted-foreground bg-secondary/60 rounded-lg px-2.5 py-1">
                            .{currentExt || 'file'}
                        </span>

                        {/* Pagination pill */}
                        {total > 1 && (
                            <span className="shrink-0 text-xs font-semibold text-muted-foreground bg-secondary/60 rounded-full px-3 py-1">
                                {current + 1} / {total}
                            </span>
                        )}

                        {/* Close */}
                        <Dialog.Close asChild>
                            <button
                                className="shrink-0 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors cursor-pointer"
                                aria-label="Close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </Dialog.Close>
                    </div>

                    {/* ══ Preview area ═════════════════════════════════════════ */}
                    <div className="flex-1 overflow-auto relative flex items-center justify-center bg-black/10 min-h-0">
                        <RenderDocumentPreview doc={doc} zoom={zoom} rotation={rotation} />

                        {/* Prev / Next overlay */}
                        {hasPrev && (
                            <button
                                onClick={() => goTo(current - 1)}
                                className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-xl transition-colors z-10"
                                aria-label="Previous document"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                        )}
                        {hasNext && (
                            <button
                                onClick={() => goTo(current + 1)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-xl transition-colors z-10"
                                aria-label="Next document"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        )}
                    </div>

                    {/* ══ Toolbar ══════════════════════════════════════════════ */}
                    <div className="shrink-0 flex items-center justify-between gap-3 px-5 py-3 border-t border-border/50 bg-secondary/10">
                        <span className="text-[11px] text-muted-foreground">
                            {doc.uploadedAt ? `Uploaded: ${formatDate(doc.uploadedAt)}` : ''}
                        </span>

                        <div className="flex items-center gap-1">
                            {/* Image-only controls: Zoom + Rotate */}
                            {showImageControls && (
                                <>
                                    <button
                                        onClick={() => setZoom((z) => Math.max(0.25, parseFloat((z - 0.25).toFixed(2))))}
                                        disabled={zoom <= 0.25}
                                        className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground disabled:opacity-30"
                                        title="Zoom Out"
                                    >
                                        <ZoomOut className="w-4 h-4" />
                                    </button>
                                    <span className="text-xs font-semibold text-muted-foreground w-12 text-center">
                                        {Math.round(zoom * 100)}%
                                    </span>
                                    <button
                                        onClick={() => setZoom((z) => Math.min(4, parseFloat((z + 0.25).toFixed(2))))}
                                        disabled={zoom >= 4}
                                        className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground disabled:opacity-30"
                                        title="Zoom In"
                                    >
                                        <ZoomIn className="w-4 h-4" />
                                    </button>
                                    <div className="w-px h-5 bg-border mx-1" />
                                    <button
                                        onClick={() => setRotation((r) => (r + 90) % 360)}
                                        className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                                        title="Rotate 90°"
                                    >
                                        <RotateCw className="w-4 h-4" />
                                    </button>
                                    <div className="w-px h-5 bg-border mx-1" />
                                </>
                            )}

                            {/* Download Button */}
                            <a
                                href={doc.url}
                                download={doc.label ? `${doc.label.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf` : 'invoice.pdf'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 transition-all shadow-xs cursor-pointer"
                                title="Download Document"
                            >
                                <Download className="w-3.5 h-3.5" />
                                <span>Download</span>
                            </a>

                            {/* Open in new tab */}
                            <a
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                                title="Open in new tab"
                            >
                                <ExternalLink className="w-4 h-4" />
                            </a>
                        </div>
                    </div>

                    {/* ══ Thumbnail strip ══════════════════════════════════════ */}
                    {total > 1 && (
                        <div className="shrink-0 flex gap-2 overflow-x-auto px-5 py-3 border-t border-border/50 bg-secondary/5">
                            {documents.map((d, idx) => (
                                <Thumbnail
                                    key={d.id}
                                    doc={d}
                                    active={idx === current}
                                    onClick={() => goTo(idx)}
                                />
                            ))}
                        </div>
                    )}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default ViewDocumentModal;
